import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import DonateButton from "../components/DonateButton";
import axios from "axios";
import { toast } from "sonner";
import { 
  Flame, 
  Trophy, 
  Target, 
  Clock, 
  Users, 
  Zap,
  ChevronRight,
  Play,
  CheckCircle,
  Star,
  Mail,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";
const COACH_HERO = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/cpnh1824__UU14268.jpg";
const COACH_ACTION = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/x5k49738__UU14252.jpg";
const COACH_ACTION_2 = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/6yfs0nxa__UU14215.jpg";
const COACH_LIFESTYLE = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/gubbzpbj__UU14196.jpg";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Track referral click on page load
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      // Store ref code for signup
      localStorage.setItem('referral_code', refCode);
      // Track the click
      axios.post(`${API}/referral/track-click?ref_code=${refCode}`).catch(() => {});
    }
  }, [searchParams]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/subscribe`, { email, source: "landing_page" });
      setSubscribed(true);
      setEmail("");
      toast.success(response.data.message || "You're in!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setSubscribing(false);
    }
  };

  const features = [
    { icon: Flame, title: "60-Day Program", desc: "Structured daily workouts that progressively challenge you" },
    { icon: Trophy, title: "Streak System", desc: "Build discipline with daily accountability tracking" },
    { icon: Target, title: "Progress Tracking", desc: "Log weight, measurements, and progress photos" },
    { icon: Clock, title: "AI Coaching", desc: "Personalized motivation when you need it most" },
    { icon: Users, title: "Leaderboard", desc: "Compete with others on the same journey" },
    { icon: Zap, title: "Exercise Library", desc: "GIF demonstrations for every movement" }
  ];

  const testimonials = [
    { name: "Marcus J.", result: "Lost 25 lbs", quote: "Coach Coop's program changed my life. The daily accountability kept me going." },
    { name: "Sarah T.", result: "Gained confidence", quote: "I've tried everything. This is the first program I actually finished." },
    { name: "David R.", result: "Built muscle", quote: "The quit-recovery feature saved me multiple times. Still going strong." }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Coach Coop" className="h-10 w-10 rounded-sm" />
              <span className="font-bold text-lg tracking-tight">COACH COOP</span>
            </Link>
            <div className="flex items-center gap-3">
              <DonateButton />
              {user ? (
                <Button 
                  data-testid="nav-dashboard-btn"
                  onClick={() => navigate("/dashboard")}
                  className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="nav-login-btn" className="uppercase font-bold tracking-widest">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button data-testid="nav-signup-btn" className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm">
                      Start Now
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src={COACH_HERO} 
            alt="Coach Coop" 
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <p className="text-primary text-sm uppercase tracking-widest mb-4 font-bold">
              Built for people who usually quit
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none mb-6">
              DON'T QUIT<br />
              <span className="text-primary">TODAY</span>
            </h1>
            <div className="text-lg text-muted-foreground mb-8 max-w-lg space-y-4">
              <p>This isn't for motivated people. It's for people who start… then disappear.</p>
              <p className="text-foreground font-medium">No choices. No browsing. No thinking.</p>
              <p>Every day you get one simple task. You either do it or you don't.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                data-testid="hero-start-btn"
                size="lg"
                onClick={() => navigate(user ? "/quitter-mode" : "/signup")}
                className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm text-lg px-8 py-6 glow-primary"
              >
                Start Today — 20 Minutes <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <div className="mt-12 p-4 bg-card/50 border border-border rounded-sm">
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-bold">If you want flexibility, this isn't it.</span><br />
                If you want structure that forces action — welcome.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-card relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4">
              HOW IT <span className="text-primary">WORKS</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              No decisions. Just action.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                step: "1", 
                title: "One Task Daily", 
                desc: "20 minutes. No browsing. No choices. Just start." 
              },
              { 
                step: "2", 
                title: "Check In Yes or No", 
                desc: "Did you show up? Tap yes or no. No paragraphs. No journaling." 
              },
              { 
                step: "3", 
                title: "See Who Showed Up", 
                desc: "Names. Checkmarks. Public accountability. You'll see when you don't." 
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-secondary/50 border-border hover:border-primary/50 transition-colors">
                  <div className="text-5xl font-black text-primary/30 mb-4">{item.step}</div>
                  <div className="text-xl font-bold text-foreground mb-2 uppercase">{item.title}</div>
                  <div className="text-muted-foreground">{item.desc}</div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Consequence Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-8 bg-zinc-900/50 border border-red-500/20 rounded-sm text-center"
          >
            <h3 className="text-xl font-bold uppercase tracking-tight mb-4 text-red-500">
              Miss a day?
            </h3>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Streak resets. Message appears: <span className="text-foreground italic">"You didn't fail. You chose not to show up. Tomorrow is still open."</span>
            </p>
            <p className="text-zinc-600 text-sm mt-4">No shaming. No comfort. Just truth.</p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4">
              EVERYTHING YOU <span className="text-accent">NEED</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete transformation system designed to get you results.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-card border-border hover:border-primary/50 transition-all hover:glow-primary group">
                  <feature.icon className="h-10 w-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold uppercase tracking-tight mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Coach Coop Section */}
      <section className="py-24 bg-card relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-6">
                MEET YOUR <span className="text-primary">TRAINER</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="text-lg">
                  <span className="text-foreground font-bold">Coach Coop</span> isn't just another fitness influencer. With over 10 years of experience as a personal trainer, boxing coach, and life coach, he's helped hundreds transform their bodies and minds.
                </p>
                <p>
                  His philosophy is simple: <span className="text-primary font-semibold">discipline over motivation</span>. Motivation fades. Discipline is what gets you through Day 22 when everyone else quits.
                </p>
                <p>
                  The 60-Day SHRED program is the culmination of a decade of training experience, designed to build not just a better body, but a stronger mind.
                </p>
              </div>
              
              <div className="flex items-center gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-black text-primary">10+</div>
                  <div className="text-sm text-muted-foreground uppercase">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-accent">500+</div>
                  <div className="text-sm text-muted-foreground uppercase">Clients Transformed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-primary">3</div>
                  <div className="text-sm text-muted-foreground uppercase">Disciplines</div>
                </div>
              </div>
              
              <Link to="/meet-coach">
                <Button 
                  variant="outline" 
                  className="mt-6 border-primary text-primary hover:bg-primary hover:text-white uppercase font-bold tracking-widest rounded-sm"
                >
                  Learn More About Coach Coop
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src={COACH_ACTION} 
                alt="Coach Coop stretching" 
                className="rounded-sm w-full h-64 object-cover border border-border"
              />
              <img 
                src={COACH_ACTION_2} 
                alt="Coach Coop training" 
                className="rounded-sm w-full h-64 object-cover border border-border mt-8"
              />
              <img 
                src={COACH_LIFESTYLE} 
                alt="Coach Coop lifestyle" 
                className="rounded-sm w-full h-64 object-cover border border-border col-span-2"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4">
              REAL <span className="text-primary">RESULTS</span>
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="p-6 bg-card border-border">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4">"{t.quote}"</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{t.name}</span>
                    <span className="text-sm text-accent font-semibold">{t.result}</span>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1637142375182-2c378fcbe061?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover opacity-10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-6 text-white">
              ONE RULE
            </h2>
            <p className="text-3xl text-white/90 mb-8 max-w-2xl mx-auto font-bold">
              Don't quit today.
            </p>
            <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
              Start today. 20 minutes. That's it.
            </p>
            <Button 
              data-testid="cta-start-btn"
              size="lg"
              onClick={() => navigate(user ? "/quitter-mode" : "/signup")}
              className="bg-white text-primary hover:bg-white/90 uppercase font-bold tracking-widest rounded-sm text-lg px-12 py-6"
            >
              START NOW <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Email Signup Section */}
      <section className="py-16 bg-card border-y border-border">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-2xl">🥊</span>
              <h3 className="text-2xl font-black uppercase tracking-tighter">
                Get <span className="text-primary">Updates</span>
              </h3>
              <span className="text-2xl">🥊</span>
            </div>
            <p className="text-muted-foreground mb-6">
              New workouts, boxing tips, and exclusive content. No spam.
            </p>
            
            {subscribed ? (
              <div className="flex items-center justify-center gap-2 text-accent">
                <CheckCircle className="h-5 w-5" />
                <span className="font-bold">You're in! Check your inbox.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 bg-secondary border-border"
                />
                <Button
                  type="submit"
                  disabled={subscribing}
                  className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest"
                >
                  {subscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Join
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">TEMPEREDHEAD FITNESS</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Control Your Temper. Transform Your Life.
            </div>
            <div className="text-sm text-muted-foreground">
              © 2024 Coach Coop Training. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
